import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { initEventTracker } from '@src/analytics/EventTracker';
import { DI_TOKENS, resolve } from '@src/di/container';
import {
  ActionStatus,
  ActionType,
  EVENT_CATEGORY_MAP,
  EventCategory,
  MonitoringService,
} from '@src/monitoring/types';
import { PostHogIntegration } from '@src/monitoring/utils/PostHogIntegration';
import { analytics } from '@src/utility/analytics';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  ActionContext,
  ActionTrackingConfig,
  EnhancedTrackEventParams,
  MonitoringContextData,
  PerformanceMetrics,
  WorkflowType,
} from '@src/monitoring/types';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

const logger = LogManager.getLogger('store:AnalyticsStore');

export class AnalyticsStore {
  private monitoringService: MonitoringService;
  private videoCreatorStore: VideoCreatorStore | undefined;

  constructor(private rootStore: RootStore) {
    makeAutoObservable<AnalyticsStore, 'activeTimers'>(this, {
      // rootStore: false,
      // Internal coordination state, not reactive: exclude from MobX deep
      // observation (markTimer mutates nested marks; nothing observes it).
      activeTimers: false,
    });

    // Initialize monitoring service with context provider
    this.monitoringService = new MonitoringService(() =>
      this.createMonitoringContextData(),
    );

    // Wire up the EventTracker so it can route to this store
    initEventTracker(this);

    // Expose Log.startTimer/endTimer app-wide (PLAT-11041/11042) without a
    // utility → monitoring import: the Logger delegates back to this store.
    LogManager.registerTimerHandlers({
      start: (key, metadata) => this.startTimer(key, metadata),
      mark: (key, markName) => this.markTimer(key, markName),
      end: (key, metadata) => this.endTimer(key, metadata),
      cancel: (key, reason) => this.cancelTimer(key, reason),
    });
  }

  // Workaround instead of getting VideoCreatorStore from DI container as it creates multiple instances of VideoCreatorStore. VideoCreatorStore passes itself to other places in it's constructor, so every time a new instance is created other places lose reference to the original store
  // TODO: proper fix after VideoCreatorStore is sliced into multiple stores in rootStore
  setVideoCreatorStore(videoCreatorStore: VideoCreatorStore) {
    this.videoCreatorStore = videoCreatorStore;
  }

  // ===== ENHANCED ACTION TRACKING METHODS =====

  /**
   * Start tracking a user action
   * @param actionName - The specific action being performed
   * @param actionType - The hierarchical type of action
   * @param workflowId - Optional workflow ID for correlation
   * @param context - Optional additional context
   * @param metadata - Optional action-specific metadata
   * @returns Transaction ID for tracking the action
   */
  startUserAction(
    actionName: string,
    actionType: ActionType = ActionType.ACTION,
    workflowId?: string,
    context?: Partial<ActionContext>,
    metadata?: Record<string, any>,
  ): string {
    return this.monitoringService.startUserAction(
      actionName,
      actionType,
      workflowId,
      context,
      metadata,
    );
  }

  /**
   * Complete a user action successfully
   * @param transactionId - The transaction ID from startUserAction
   * @param metadata - Optional additional metadata
   * @param performance - Optional performance metrics
   */
  completeUserAction(
    transactionId: string,
    metadata?: Record<string, any>,
    performance?: Partial<PerformanceMetrics>,
  ): void {
    this.monitoringService.completeUserAction(
      transactionId,
      metadata,
      performance,
    );
  }

  /**
   * Fail a user action with error context
   * @param transactionId - The transaction ID from startUserAction
   * @param error - Error object or message
   * @param metadata - Optional additional metadata
   */
  failUserAction(
    transactionId: string,
    error: Error | string,
    metadata?: Record<string, any>,
  ): void {
    this.monitoringService.failUserAction(transactionId, error, metadata);
  }

  /**
   * Cancel a user action
   * @param transactionId - The transaction ID from startUserAction
   * @param reason - Optional cancellation reason
   */
  cancelUserAction(transactionId: string, reason?: string): void {
    this.monitoringService.cancelUserAction(transactionId, reason);
  }

  // ===== TRANSACTION TIMERS (Log.startTimer / Log.endTimer) =====

  /**
   * PLAT-11041/11042: measure real user-facing transactions.
   * startTimer(key) stamps the start; endTimer(key) emits the action pair
   * (`<key>` + `<key>_performance`) with the true elapsed duration in
   * performance.duration — unlike most *_performance events, which carry ~0.
   * markTimer records intermediate gates (e.g. renderer ready) as metadata
   * so a slow transaction self-explains. A timer that is never ended is
   * dropped on cancelTimer (emitting a cancelled action with elapsed_ms).
   */
  private activeTimers = new Map<
    string,
    {
      startedAt: number;
      metadata?: Record<string, unknown>;
      marks: Record<string, number>;
    }
  >();

  /** Start (or restart — a newer start supersedes) a named transaction timer. */
  startTimer(key: string, metadata?: Record<string, unknown>): void {
    // A replaced timer must not vanish silently (e.g. a second clip opened
    // before the first became usable): emit its cancellation, with elapsed
    // time, before starting the new one. No-op when no timer is running.
    this.cancelTimer(key, 'superseded');
    this.activeTimers.set(key, {
      startedAt: performance.now(),
      metadata,
      marks: {},
    });
  }

  /** Record an intermediate gate on a running timer (elapsed ms since start). */
  markTimer(key: string, markName: string): void {
    const timer = this.activeTimers.get(key);
    if (!timer) {
      return;
    }
    timer.marks[markName] = Math.round(performance.now() - timer.startedAt);
  }

  /**
   * End a running timer and emit the timed action with its true duration.
   * Emits through trackEvent as OPERATIONAL: the direct datadogRum.addAction
   * route is unsampled — the ActionTracker path samples ActionType.EVENT at
   * LOW (10%) and drops unregistered names as behavioral, which silently
   * swallowed these events in testing.
   */
  endTimer(
    key: string,
    metadata?: Record<string, unknown>,
    endedAtMs?: number,
  ): void {
    const timer = this.activeTimers.get(key);
    if (!timer) {
      return;
    }
    this.activeTimers.delete(key);
    // endedAtMs (performance.now() timestamp) lets callers that confirm
    // usability via a stability window report the moment the transaction
    // actually completed rather than when the confirmation fired.
    const duration = (endedAtMs ?? performance.now()) - timer.startedAt;
    this.trackEvent({
      eventName: key,
      label: key,
      eventCategory: EventCategory.OPERATIONAL,
      extraProperties: {
        ...timer.metadata,
        ...metadata,
        ...timer.marks,
        duration_ms: Math.round(duration),
      },
      performance: { duration },
    });
  }

  /**
   * Emit a `<key>_timeout` beacon for a timer that is STILL running —
   * "this transaction has not completed after elapsed_ms". Does not stop
   * the timer: if the transaction eventually completes, the true duration
   * is still reported by endTimer. No-op if the timer already ended.
   */
  reportTimerStillRunning(
    key: string,
    metadata?: Record<string, unknown>,
  ): void {
    const timer = this.activeTimers.get(key);
    if (!timer) {
      return;
    }
    const elapsedMs = Math.round(performance.now() - timer.startedAt);
    this.trackEvent({
      eventName: `${key}_timeout`,
      label: key,
      eventCategory: EventCategory.OPERATIONAL,
      // A still-running transaction past its deadline is a failure state,
      // not a success; the timer stays active so a late endTimer can still
      // report the true completion.
      status: ActionStatus.FAILURE,
      errorContext: { errorMessage: `${key} still running after timeout` },
      extraProperties: {
        ...timer.metadata,
        ...metadata,
        // Marks show which gates HAD completed before the hang — on a
        // timeout this is the diagnostic that names the stuck gate.
        ...timer.marks,
        elapsed_ms: elapsedMs,
      },
    });
  }

  /** Cancel a running timer (e.g. user gave up / closed the surface). */
  cancelTimer(key: string, reason?: string): void {
    const timer = this.activeTimers.get(key);
    if (!timer) {
      return;
    }
    this.activeTimers.delete(key);
    const elapsedMs = Math.round(performance.now() - timer.startedAt);
    this.trackEvent({
      eventName: `${key}_cancelled`,
      label: reason || key,
      eventCategory: EventCategory.OPERATIONAL,
      // Record the enhanced-monitoring action as cancelled, not success.
      status: ActionStatus.CANCELLED,
      extraProperties: {
        ...timer.metadata,
        // Keep the per-gate readiness breakdown on early closes too.
        ...timer.marks,
        elapsed_ms: elapsedMs,
        cancellation_reason: reason,
      },
    });
  }

  // ===== WORKFLOW TRACKING METHODS =====

  /**
   * Start a new workflow
   * @param workflowType - The type of workflow being started
   * @param metadata - Optional workflow metadata
   * @param context - Optional additional context
   * @returns Workflow ID for tracking related actions
   */
  startWorkflow(
    workflowType: WorkflowType,
    metadata?: Record<string, any>,
    context?: Partial<ActionContext>,
  ): string {
    return this.monitoringService.startWorkflow(
      workflowType,
      metadata,
      context,
    );
  }

  /**
   * Complete a workflow
   * @param workflowId - The workflow ID from startWorkflow
   * @param success - Whether the workflow completed successfully
   * @param metadata - Optional completion metadata
   */
  completeWorkflow(
    workflowId: string,
    success: boolean,
    metadata?: Record<string, any>,
  ): void {
    this.monitoringService.completeWorkflow(workflowId, success, metadata);
  }

  /**
   * Cancel a workflow
   * @param workflowId - The workflow ID from startWorkflow
   * @param reason - Optional cancellation reason
   */
  cancelWorkflow(workflowId: string, reason?: string): void {
    this.monitoringService.cancelWorkflow(workflowId, reason);
  }

  setPostHogAIObservabilityEnabled(value: boolean) {
    PostHogIntegration.setAIObservabilityEnabled(value);
  }

  setContext(
    storyId: string,
    orgSlug: string | undefined,
    orgTitle?: string,
    orgId?: string,
  ) {
    // Datadog context
    datadogRum.setGlobalContextProperty('organization', orgSlug);
    datadogLogs.setGlobalContextProperty('organization', orgSlug);
    datadogRum.setGlobalContextProperty('storyId', storyId);
    datadogLogs.setGlobalContextProperty('storyId', storyId);

    // Org metadata for Datadog (set when available)
    if (orgTitle) {
      datadogRum.setGlobalContextProperty('organization_name', orgTitle);
    }
    if (orgId) {
      datadogRum.setGlobalContextProperty('organization_id', orgId);
    }

    // PostHog context (mirrors Datadog global context properties)
    PostHogIntegration.group('organization', orgSlug ?? 'unknown', {
      name: orgTitle,
      slug: orgSlug,
      id: orgId,
    });
    PostHogIntegration.setContext({
      storyId,
      organization: orgSlug,
    });

    // Segment page view
    analytics.page();
  }

  getTrackedUserData() {
    const userData = this.rootStore.userIdentityStore.currentIdentity;
    return {
      ...(userData && {
        user: {
          email: userData.email,
          name: userData.name,
        },
      }),
    };
  }

  getTrackedEnvData() {
    return {
      env: process.env.REACT_APP_DATO_ENV || 'dev',
    };
  }

  /**
   * Enhanced track event method with backward compatibility
   * Supports both legacy string event names and new EventName enum
   */
  trackEvent({
    eventName,
    label,
    extraProperties = {},
    logMethod = 'log',
    transactionId,
    actionType = ActionType.ACTION,
    eventCategory,
    workflowId,
    status = ActionStatus.SUCCESS,
    performance,
    errorContext,
  }: EnhancedTrackEventParams) {
    // Legacy tracking (maintain backward compatibility)
    const trackedProperties = {
      datetime: new Date().toISOString(),
      ...this.getTrackedUserData(),
      ...this.getTrackedEnvData(),
      ...extraProperties,
    };

    // Determine event category for routing
    const determinedEventCategory =
      eventCategory || this.getEventCategory(eventName);

    // Route to appropriate analytics systems based on category
    this.routeEventToAnalyticsSystems(
      eventName,
      trackedProperties,
      determinedEventCategory,
    );

    // Legacy console logging
    const eventLog = {
      level: 'info',
      eventName,
      label,
      transactionId,
      eventCategory: determinedEventCategory,
      analyticsContext: {
        ...trackedProperties,
      },
    };
    console[logMethod](eventLog);

    // Enhanced tracking (new monitoring infrastructure)
    if (this.getMonitoringConfig().enabled && eventName) {
      try {
        const actionTransactionId =
          transactionId ||
          this.startUserAction(
            eventName,
            actionType,
            workflowId,
            undefined,
            extraProperties,
          );

        if (actionTransactionId) {
          // Handle different action statuses
          switch (status) {
            case ActionStatus.SUCCESS:
              this.completeUserAction(
                actionTransactionId,
                extraProperties,
                performance,
              );
              break;
            case ActionStatus.FAILURE:
              this.failUserAction(
                actionTransactionId,
                errorContext?.errorMessage || label,
                extraProperties,
              );
              break;
            case ActionStatus.CANCELLED:
              this.cancelUserAction(actionTransactionId, label);
              break;
            case ActionStatus.STARTED:
              // Action already started, no additional action needed
              break;
          }
        }
      } catch (error) {
        logger.warn(
          'Enhanced tracking failed, falling back to legacy tracking:',
          error,
        );
      }
    }
  }

  // ===== CONFIGURATION AND UTILITY METHODS =====

  /**
   * Create monitoring context data from RootStore and VideoCreatorStore
   * Acts as an adapter between stores and monitoring utilities
   * Uses dependency injection to resolve VideoCreatorStore when available
   */
  private createMonitoringContextData(): MonitoringContextData {
    try {
      // Resolve RootStore from DI container first, fallback to constructor parameter
      let rootStore;
      try {
        rootStore = resolve(DI_TOKENS.ROOT_STORE);
      } catch (error) {
        // DI container not initialized yet, use constructor parameter
        rootStore = this.rootStore;
      }
      const userIdentity = rootStore.userIdentityStore?.currentIdentity;

      // TODO: figure out why DI container resolve creates new instances of VideoCreatorStore here
      // Resolve VideoCreatorStore from DI container
      // let videoCreator
      // try {
      //   // videoCreator = resolve(DI_TOKENS.VIDEO_CREATOR_STORE)
      // } catch (error) {
      //   // DI container not initialized yet or VideoCreatorStore not available
      //   console.debug(
      //     'VideoCreatorStore not available for context enrichment:',
      //     error,
      //   );
      //   videoCreator = null;
      // }

      const { story, currentVideo, organization } =
        this.videoCreatorStore || {};

      return {
        user: userIdentity
          ? {
              id: userIdentity.id,
              email: userIdentity.email,
              name: userIdentity.name,
              role: (userIdentity as any).role,
              type: (userIdentity as any).type,
              source: userIdentity.source,
              environment: rootStore.userIdentityStore?.environment,
            }
          : undefined,
        video: this.videoCreatorStore
          ? {
              story: story
                ? {
                    id: story.id,
                    title: story.title,
                  }
                : undefined,
              currentVideo: currentVideo?.id
                ? {
                    id: currentVideo.id,
                    title: currentVideo.title,
                  }
                : undefined,
            }
          : undefined,
        organization: organization
          ? {
              id: organization.id,
              name: organization.title || '',
              title: organization.title,
              slug: organization.slug,
            }
          : undefined,
      };
    } catch (error) {
      logger.warn('Failed to create monitoring context data:', error);
      return {};
    }
  }

  /**
   * Update monitoring configuration
   */
  updateMonitoringConfig(newConfig: Partial<ActionTrackingConfig>): void {
    this.monitoringService.updateMonitoringConfig(newConfig);
  }

  /**
   * Get current monitoring configuration
   */
  getMonitoringConfig(): ActionTrackingConfig {
    return this.monitoringService.getMonitoringConfig();
  }

  /**
   * Track performance metrics
   */
  trackPerformance(
    actionName: string,
    metrics: PerformanceMetrics,
    context?: Partial<ActionContext>,
  ): void {
    this.monitoringService.trackPerformance(actionName, metrics, context);
  }

  /**
   * Get event category for routing to appropriate monitoring system
   */
  private getEventCategory(eventName: string): EventCategory {
    return EVENT_CATEGORY_MAP[eventName] || EventCategory.BEHAVIORAL;
  }

  /**
   * Route events to appropriate analytics systems based on category
   */
  private routeEventToAnalyticsSystems(
    eventName: string,
    trackedProperties: Record<string, any>,
    eventCategory: EventCategory,
  ): void {
    if (!eventName) return;

    // Behavioral and critical events -> PostHog + Datadog
    if (
      eventCategory === EventCategory.BEHAVIORAL ||
      eventCategory === EventCategory.CRITICAL
    ) {
      PostHogIntegration.capture(eventName, trackedProperties);
      datadogRum.addAction(eventName, {
        ...trackedProperties,
        eventCategory,
      });
    }

    // Operational -> Datadog only
    if (eventCategory === EventCategory.OPERATIONAL) {
      datadogRum.addAction(eventName, {
        ...trackedProperties,
        eventCategory,
        operationalFocus: true,
      });
    }
  }

  log({
    level,
    label,
    transactionId,
    msg,
    extraProperties,
  }: {
    level: 'debug' | 'info' | 'warn' | 'error';
    label: string;
    transactionId: string | null;
    msg: string;
    extraProperties: Record<string, any>;
  }) {
    const trackedProperties = {
      datetime: new Date().toISOString(),
      ...this.getTrackedUserData(),
      ...this.getTrackedEnvData(),
      ...extraProperties,
    };
    const eventLog = {
      level,
      label,
      transactionId,
      msg,
      analyticsContext: {
        ...trackedProperties,
      },
    };
    console[level](eventLog);
  }
}
